import * as React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import AreaShell from '../../chrome/AreaShell';
import useAuthenticatedApi from '../../../Authentication/components/hooks/useAuthenticatedApi';
import { Checkbox, ConfirmDialog, Dialog, Field, TextInput, Toggle } from '../../components/forms';
import { TCard, TEmpty, TFoot, TRow, TSkeleton, Toolbar } from '../../components/table';
import { useToast } from '../../components/roster';
import './admin-ops.css';

/*
 * Gallery users: consumer accounts behind face recognition and photo
 * rollups. The server texts/emails them: a welcome message on create, and
 * the rollup link on push. Bulk push messages every flagged user and only
 * answers once all are sent. Reference photos upload without a crop step.
 */

interface GalleryUser {
    id: number;
    firstName: string;
    lastName: string;
    email: string | null;
    phoneNumber: string | null;
    igHandle: string | null;
    slug?: string | null;
    facialRecognitionOptedIn: boolean;
    needsRollupCommunication: boolean;
}

interface RefPhoto {
    id: number;
    photoUrl?: string;
    uri?: string;
    url?: string;
    photoUri?: string;
}

const PAGE = 50;

const EMPTY_DRAFT = {
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    igHandle: '',
    slug: '',
    facialRecognitionOptedIn: false,
};

const nameOf = (u: GalleryUser) => [u.firstName, u.lastName].filter(Boolean).join(' ') || u.email || 'Unnamed';
const reach = (u: GalleryUser) => [u.email, u.phoneNumber].filter(Boolean).join(' · ');
const channels = (u: GalleryUser) => {
    const c = [u.phoneNumber && `a text to ${u.phoneNumber}`, u.email && `an email to ${u.email}`].filter(Boolean);
    return c.length ? c.join(' and ') : 'a message (no email or phone on file, so nothing may arrive)';
};
const photoSrc = (p: RefPhoto) => p.photoUrl ?? p.uri ?? p.url ?? p.photoUri;
const serverError = (e: any) => {
    const d = e?.response?.data;
    return typeof d?.error === 'string' ? d.error : typeof d?.message === 'string' ? d.message : null;
};

export default function AdminGalleryUsersV3() {
    const api = useAuthenticatedApi();
    const toast = useToast();

    const [users, setUsers] = useState<GalleryUser[] | null>(null);
    const [failed, setFailed] = useState(false);
    const [query, setQuery] = useState('');
    const [limit, setLimit] = useState(PAGE);

    const load = useCallback(() => {
        api.get('/gallery-users')
            .then((r: any) => {
                setUsers(Array.isArray(r.data) ? r.data : []);
                setFailed(false);
            })
            .catch(() => setFailed(true));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    useEffect(load, [load]);

    const q = query.trim().toLowerCase();
    const visible = useMemo(
        () =>
            (users ?? []).filter(
                (u) =>
                    !q ||
                    `${u.firstName} ${u.lastName} ${u.email ?? ''} ${u.phoneNumber ?? ''} ${u.igHandle ?? ''}`
                        .toLowerCase()
                        .includes(q),
            ),
        [users, q],
    );
    const flagged = useMemo(() => (users ?? []).filter((u) => u.needsRollupCommunication), [users]);

    /* ---- create / edit ---- */
    const [dialog, setDialog] = useState<{ kind: 'create' } | { kind: 'edit'; user: GalleryUser } | null>(null);
    const [draft, setDraft] = useState(EMPTY_DRAFT);
    const [busy, setBusy] = useState(false);
    const [dialogError, setDialogError] = useState('');
    const [tried, setTried] = useState(false);
    const set = (k: keyof typeof EMPTY_DRAFT) => (e: React.ChangeEvent<HTMLInputElement>) =>
        setDraft((d) => ({ ...d, [k]: e.target.value }));

    /* reference photos for the user being edited; late replies for another user are ignored */
    const [photos, setPhotos] = useState<RefPhoto[] | null>(null);
    const [photosFailed, setPhotosFailed] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [photoError, setPhotoError] = useState('');
    const fileRef = useRef<HTMLInputElement>(null);
    const wanted = useRef<number | null>(null);

    const loadPhotos = (id: number) => {
        setPhotosFailed(false);
        return api
            .get(`/gallery-users/${id}/reference-photos`)
            .then((r: any) => {
                if (wanted.current === id) setPhotos(Array.isArray(r.data) ? r.data : []);
            })
            .catch(() => {
                if (wanted.current !== id) return;
                setPhotos([]);
                setPhotosFailed(true);
            });
    };

    const openCreate = () => {
        wanted.current = null;
        setDraft(EMPTY_DRAFT);
        setDialogError('');
        setTried(false);
        setDialog({ kind: 'create' });
    };

    const openEdit = (user: GalleryUser) => {
        wanted.current = user.id;
        setDraft({
            firstName: user.firstName ?? '',
            lastName: user.lastName ?? '',
            email: user.email ?? '',
            phoneNumber: user.phoneNumber ?? '',
            igHandle: user.igHandle ?? '',
            slug: user.slug ?? '',
            facialRecognitionOptedIn: !!user.facialRecognitionOptedIn,
        });
        setPhotos(null);
        setPhotoError('');
        setDialogError('');
        setTried(false);
        setDialog({ kind: 'edit', user });
        loadPhotos(user.id);
    };

    const closeDialog = () => {
        if (busy) return;
        wanted.current = null;
        setDialog(null);
    };

    const nameMissing = !draft.firstName.trim() || !draft.lastName.trim();
    const emailBad = draft.email.trim() !== '' && !/^\S+@\S+\.\S+$/.test(draft.email.trim());

    const submit = async () => {
        if (!dialog || busy) return;
        setTried(true);
        if (nameMissing || emailBad) return;
        setBusy(true);
        setDialogError('');
        const body: any = {
            firstName: draft.firstName.trim(),
            lastName: draft.lastName.trim(),
            email: draft.email.trim() || null,
            phoneNumber: draft.phoneNumber.trim() || null,
            igHandle: draft.igHandle.trim() || null,
            facialRecognitionOptedIn: draft.facialRecognitionOptedIn,
        };
        if (dialog.kind === 'edit') body.slug = draft.slug.trim() || null;
        try {
            if (dialog.kind === 'create') await api.post('/gallery-users', body);
            else await api.put(`/gallery-users/${dialog.user.id}`, body);
            wanted.current = null;
            setDialog(null);
            toast.show(dialog.kind === 'create' ? `Added ${body.firstName} ${body.lastName}.` : 'Saved.');
            load();
        } catch (e) {
            setDialogError(serverError(e) ?? 'Couldn’t save. Try again.');
        } finally {
            setBusy(false);
        }
    };

    const uploadPhoto = async (file: File) => {
        if (dialog?.kind !== 'edit' || uploading) return;
        const id = dialog.user.id;
        setUploading(true);
        setPhotoError('');
        const formData = new FormData();
        formData.append('photo', file);
        try {
            await api.post(`/gallery-users/${id}/reference-photo`, formData);
            await loadPhotos(id);
        } catch {
            setPhotoError('Couldn’t upload the photo. Use a JPEG or PNG and try again.');
        } finally {
            setUploading(false);
        }
    };

    const removePhoto = async (photo: RefPhoto) => {
        const prev = photos;
        setPhotoError('');
        setPhotos((cur) => (cur ?? []).filter((p) => p.id !== photo.id));
        try {
            await api.delete(`/gallery-users/reference-photo/${photo.id}`);
        } catch {
            setPhotos(prev);
            setPhotoError('Couldn’t delete the photo.');
        }
    };

    /* ---- delete ---- */
    const [removing, setRemoving] = useState<GalleryUser | null>(null);
    const remove = async (user: GalleryUser) => {
        const prev = users;
        setUsers((cur) => (cur ?? []).filter((u) => u.id !== user.id));
        try {
            await api.delete(`/gallery-users/${user.id}`);
            toast.show(`Deleted ${nameOf(user)}.`);
        } catch {
            setUsers(prev);
            toast.show(`Couldn’t delete ${nameOf(user)}.`);
        }
    };

    /* ---- push rollups (these send real texts/emails) ---- */
    const [pushing, setPushing] = useState<GalleryUser | null>(null);
    const [pushingId, setPushingId] = useState<number | null>(null);
    const pushRollup = async (user: GalleryUser) => {
        if (pushingId !== null) return;
        setPushingId(user.id);
        try {
            await api.post(`/gallery-users/${user.id}/push-rollup`);
            toast.show(`Rollup sent to ${nameOf(user)}.`);
            load();
        } catch {
            toast.show(`Couldn’t send the rollup to ${nameOf(user)}.`);
        } finally {
            setPushingId(null);
        }
    };

    const [bulkOpen, setBulkOpen] = useState(false);
    const [bulkAck, setBulkAck] = useState(false);
    const [pushingAll, setPushingAll] = useState(false);
    const [bulkError, setBulkError] = useState('');
    const pushAll = async () => {
        if (pushingAll || !bulkAck) return;
        setPushingAll(true);
        setBulkError('');
        try {
            await api.post('/gallery-users/push-rollups');
            setBulkOpen(false);
            toast.show('Rollups sent to every flagged gallery user.');
            load();
        } catch {
            // some messages may already have gone out before the failure
            setBulkError('The bulk push failed partway or didn’t start. Reload to see who is still flagged before trying again.');
            load();
        } finally {
            setPushingAll(false);
        }
    };

    const editing = dialog?.kind === 'edit' ? dialog.user : null;

    return (
        <AreaShell
            area="admin"
            tab="gallery-users"
            sub="Consumer accounts behind face recognition and photo rollups"
            actions={
                <>
                    <button
                        type="button"
                        className="tb-btn"
                        disabled={users === null || pushingAll}
                        onClick={() => {
                            setBulkAck(false);
                            setBulkError('');
                            setBulkOpen(true);
                        }}
                    >
                        {pushingAll ? 'Sending rollups…' : 'Push all rollups…'}
                    </button>
                    <button type="button" className="tb-new" onClick={openCreate}>
                        Add user
                    </button>
                </>
            }
        >
            <div className="ao-page">
                <div>
                    <Toolbar
                        searchValue={query}
                        onSearch={(v) => {
                            setQuery(v);
                            setLimit(PAGE);
                        }}
                        searchPlaceholder="Search name, email, phone, or handle"
                        filterCount={0}
                    />
                    {failed ? (
                        <TCard>
                            <TEmpty message="Couldn’t load gallery users." actionLabel="Retry" onAction={load} />
                        </TCard>
                    ) : users === null ? (
                        <TCard>
                            <TSkeleton rows={5} />
                        </TCard>
                    ) : visible.length === 0 ? (
                        <TCard>
                            <TEmpty
                                message={users.length === 0 ? 'No gallery users yet.' : `Nothing matches “${query}”.`}
                                actionLabel={users.length === 0 ? 'Add user' : undefined}
                                onAction={openCreate}
                            />
                        </TCard>
                    ) : (
                        <TCard>
                            {visible.slice(0, limit).map((u, i) => (
                                <TRow
                                    key={u.id}
                                    index={i}
                                    name={nameOf(u)}
                                    badge={u.facialRecognitionOptedIn ? 'Face' : undefined}
                                    meta={reach(u) || 'No email or phone'}
                                    metaDate={u.igHandle ? `@${u.igHandle.replace(/^@/, '')}` : undefined}
                                    date={u.igHandle ? `@${u.igHandle.replace(/^@/, '')}` : ''}
                                    status={pushingId === u.id ? 'Sending…' : u.needsRollupCommunication ? 'Rollup due' : ''}
                                    onOpen={() => openEdit(u)}
                                    menuItems={[
                                        { label: 'Edit', onClick: () => openEdit(u) },
                                        ...(u.needsRollupCommunication
                                            ? [{ label: 'Send rollup…', onClick: () => setPushing(u) }]
                                            : []),
                                        { label: 'Delete…', onClick: () => setRemoving(u) },
                                    ]}
                                />
                            ))}
                        </TCard>
                    )}
                    {!failed && users !== null && users.length > 0 && (
                        <TFoot>
                            <span className="ao-more">
                                <span>
                                    {visible.length > limit
                                        ? `${limit} of ${visible.length}${q ? ' · filtered' : ''}`
                                        : q
                                          ? `${visible.length} of ${users.length} · filtered`
                                          : `${users.length} ${users.length === 1 ? 'user' : 'users'} · ${flagged.length} rollup due`}
                                </span>
                                {visible.length > limit && (
                                    <button type="button" className="tb-btn" onClick={() => setLimit((l) => l + PAGE)}>
                                        Show more
                                    </button>
                                )}
                            </span>
                        </TFoot>
                    )}
                </div>
            </div>

            <Dialog
                open={dialog !== null}
                title={editing ? `Edit ${nameOf(editing)}` : 'Add gallery user'}
                onClose={closeDialog}
                onSubmit={submit}
                footer={
                    <>
                        <button type="button" className="tb-btn" onClick={closeDialog} disabled={busy}>
                            Cancel
                        </button>
                        <button type="submit" className="tb-new" disabled={busy || uploading}>
                            {busy ? 'Saving…' : editing ? 'Save' : 'Add and send welcome'}
                        </button>
                    </>
                }
            >
                {!editing && (
                    <p className="ao-note" style={{ marginBottom: 14 }}>
                        Adding someone sends them a welcome text and/or email with their gallery link right away.
                    </p>
                )}
                <div className="two">
                    <Field label="First name" error={tried && !draft.firstName.trim() ? 'Required' : undefined}>
                        <TextInput autoFocus value={draft.firstName} onChange={set('firstName')} disabled={busy} />
                    </Field>
                    <Field label="Last name" error={tried && !draft.lastName.trim() ? 'Required' : undefined}>
                        <TextInput value={draft.lastName} onChange={set('lastName')} disabled={busy} />
                    </Field>
                </div>
                <div className="two" style={{ marginTop: 14 }}>
                    <Field label="Email" error={emailBad ? 'That doesn’t look like an email address.' : undefined}>
                        <TextInput type="email" inputMode="email" value={draft.email} onChange={set('email')} disabled={busy} />
                    </Field>
                    <Field label="Phone">
                        <TextInput type="tel" inputMode="tel" value={draft.phoneNumber} onChange={set('phoneNumber')} disabled={busy} />
                    </Field>
                </div>
                <div className="two" style={{ marginTop: 14 }}>
                    <Field label="Instagram">
                        <TextInput placeholder="handle" value={draft.igHandle} onChange={set('igHandle')} disabled={busy} />
                    </Field>
                    {editing && (
                        <Field label="Slug" help="Their gallery link: /photos/<slug>">
                            <TextInput className="input ao-mono" value={draft.slug} onChange={set('slug')} disabled={busy} />
                        </Field>
                    )}
                </div>
                <div style={{ marginTop: 14 }}>
                    <Toggle
                        label="Face recognition opted in"
                        checked={draft.facialRecognitionOptedIn}
                        disabled={busy}
                        onChange={(e) => {
                            const on = e.target.checked;
                            setDraft((d) => ({ ...d, facialRecognitionOptedIn: on }));
                        }}
                    />
                </div>

                {editing && draft.facialRecognitionOptedIn && (
                    <div style={{ marginTop: 16 }}>
                        <p className="s-lab" style={{ margin: '0 0 4px' }}>Reference photos</p>
                        <p className="field-h" style={{ marginTop: 0 }}>
                            These are biometric data: face recognition matches this person’s face against event photos.
                            Upload only with their consent, and delete them if they opt out. JPEG or PNG; uploaded as-is,
                            no cropping.
                        </p>
                        {photos === null ? (
                            <p className="quiet-empty" style={{ marginTop: 8 }}>Loading photos…</p>
                        ) : (
                            <div className="ao-photos">
                                {photos.map((p, i) => (
                                    <span className="ao-photo" key={p.id}>
                                        <img className="thumb" src={photoSrc(p)} alt={`${nameOf(editing)}, reference ${i + 1}`} />
                                        <button
                                            type="button"
                                            className="ao-x"
                                            aria-label="Delete reference photo"
                                            title="Delete reference photo"
                                            disabled={busy}
                                            onClick={() => removePhoto(p)}
                                        >
                                            ×
                                        </button>
                                    </span>
                                ))}
                                <button
                                    type="button"
                                    className="ao-add"
                                    aria-label="Upload reference photo"
                                    title="Upload reference photo"
                                    disabled={uploading || busy}
                                    onClick={() => fileRef.current?.click()}
                                >
                                    {uploading ? '…' : '+'}
                                </button>
                                <input
                                    ref={fileRef}
                                    type="file"
                                    accept="image/jpeg,image/png"
                                    hidden
                                    aria-label="Reference photo file"
                                    onChange={(e) => {
                                        const f = e.target.files?.[0];
                                        if (f) uploadPhoto(f);
                                        e.target.value = '';
                                    }}
                                />
                            </div>
                        )}
                        {photosFailed && <span className="field-e">Couldn’t load reference photos.</span>}
                        {photoError && <span className="field-e" role="alert">{photoError}</span>}
                    </div>
                )}

                {dialogError && (
                    <p className="ao-note err" role="alert" style={{ marginTop: 14 }}>
                        {dialogError}
                    </p>
                )}
            </Dialog>

            <ConfirmDialog
                open={removing !== null}
                title={`Delete ${removing ? nameOf(removing) : ''}?`}
                body="Their account and reference photos are deleted. This can’t be undone."
                actionLabel="Delete"
                onConfirm={() => removing && remove(removing)}
                onClose={() => setRemoving(null)}
            />

            <ConfirmDialog
                open={pushing !== null}
                danger={false}
                title={`Send rollup to ${pushing ? nameOf(pushing) : ''}?`}
                body={
                    pushing
                        ? `This sends ${nameOf(pushing)} ${channels(pushing)} now, with a link to their gallery. It can’t be unsent.`
                        : ''
                }
                actionLabel="Send rollup"
                onConfirm={() => pushing && pushRollup(pushing)}
                onClose={() => setPushing(null)}
            />

            <Dialog
                open={bulkOpen}
                title="Push rollups to every eligible gallery user?"
                onClose={() => {
                    if (!pushingAll) setBulkOpen(false);
                }}
                onSubmit={pushAll}
                footer={
                    <>
                        <button type="button" className="tb-btn" disabled={pushingAll} onClick={() => setBulkOpen(false)}>
                            Cancel
                        </button>
                        <button type="submit" className="tb-new danger" disabled={!bulkAck || pushingAll}>
                            {pushingAll ? 'Sending…' : 'Send to everyone flagged'}
                        </button>
                    </>
                }
            >
                <p style={{ margin: 0 }}>
                    This texts and/or emails <b>every gallery user flagged “rollup due”</b>, across all orgs, with a
                    link to their gallery. It goes by the server’s flags at the moment you send, not just this list
                    {users ? ` (${flagged.length} flagged here right now)` : ''}. Messages can’t be unsent, and the
                    request stays open until every message has gone out.
                </p>
                <div style={{ marginTop: 14 }}>
                    <Checkbox
                        label="I understand this messages every eligible gallery user."
                        checked={bulkAck}
                        disabled={pushingAll}
                        onChange={(e) => setBulkAck(e.target.checked)}
                    />
                </div>
                {bulkError && (
                    <p className="ao-note err" role="alert" style={{ marginTop: 14 }}>
                        {bulkError}
                    </p>
                )}
            </Dialog>

            {toast.node}
        </AreaShell>
    );
}
